/**
 * Customer Sentiment Tracker Widget - Premium Liquid Crystal Design
 * ===================================================================
 * Live review aggregation from Google, TripAdvisor, etc.
 * Sentiment trends and AI-categorized feedback themes.
 * Apple-inspired glassmorphism.
 */

import React, { useState } from 'react';
import { Progress } from '@/components/ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Star, TrendingUp, TrendingDown, MessageSquare, ThumbsUp, ThumbsDown,
  ChevronRight, Smile, Meh, Frown, Brain, ExternalLink, AlertTriangle
} from 'lucide-react';
import { ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip, Area, AreaChart } from 'recharts';

import { INDUSTRY_REVIEWS, getIndustryData } from '@/config/industryDemoData';

// Industry-specific themes
const INDUSTRY_THEMES = {
  hospitality: [
    { name: 'Food Quality', mentions: 324, sentiment: 92, trend: '+5%' },
    { name: 'Service', mentions: 287, sentiment: 88, trend: '+3%' },
    { name: 'Atmosphere', mentions: 198, sentiment: 95, trend: '+2%' },
    { name: 'Value', mentions: 156, sentiment: 72, trend: '-4%' },
    { name: 'Wait Time', mentions: 89, sentiment: 65, trend: '-2%' },
  ],
  retail: [
    { name: 'Product Quality', mentions: 412, sentiment: 91, trend: '+4%' },
    { name: 'Staff Helpfulness', mentions: 298, sentiment: 89, trend: '+6%' },
    { name: 'Store Layout', mentions: 187, sentiment: 86, trend: '+2%' },
    { name: 'Pricing', mentions: 234, sentiment: 68, trend: '-3%' },
    { name: 'Delivery Speed', mentions: 156, sentiment: 72, trend: '+5%' },
  ],
  healthcare: [
    { name: 'Doctor Quality', mentions: 356, sentiment: 94, trend: '+3%' },
    { name: 'Wait Time', mentions: 278, sentiment: 62, trend: '-2%' },
    { name: 'Facilities', mentions: 189, sentiment: 91, trend: '+4%' },
    { name: 'Billing Clarity', mentions: 145, sentiment: 65, trend: '+1%' },
    { name: 'Bedside Manner', mentions: 212, sentiment: 88, trend: '+5%' },
  ],
  property: [
    { name: 'Sales Results', mentions: 189, sentiment: 94, trend: '+6%' },
    { name: 'Communication', mentions: 256, sentiment: 82, trend: '+3%' },
    { name: 'Market Knowledge', mentions: 178, sentiment: 96, trend: '+2%' },
    { name: 'Negotiation', mentions: 134, sentiment: 90, trend: '+4%' },
    { name: 'Response Time', mentions: 98, sentiment: 68, trend: '-5%' },
  ],
  technology: [
    { name: 'Code Quality', mentions: 124, sentiment: 92, trend: '+5%' },
    { name: 'Delivery Speed', mentions: 98, sentiment: 78, trend: '-2%' },
    { name: 'Communication', mentions: 156, sentiment: 86, trend: '+4%' },
    { name: 'Post-Launch Support', mentions: 87, sentiment: 70, trend: '-3%' },
    { name: 'Innovation', mentions: 112, sentiment: 94, trend: '+6%' },
  ],
  construction: [
    { name: 'Build Quality', mentions: 98, sentiment: 91, trend: '+3%' },
    { name: 'Timeline', mentions: 76, sentiment: 65, trend: '-4%' },
    { name: 'Safety Standards', mentions: 56, sentiment: 96, trend: '+2%' },
    { name: 'Budget Accuracy', mentions: 67, sentiment: 72, trend: '+1%' },
    { name: 'Communication', mentions: 89, sentiment: 78, trend: '+5%' },
  ],
  fitness: [
    { name: 'Equipment', mentions: 234, sentiment: 88, trend: '+3%' },
    { name: 'Trainer Quality', mentions: 312, sentiment: 94, trend: '+5%' },
    { name: 'Cleanliness', mentions: 198, sentiment: 92, trend: '+2%' },
    { name: 'Class Variety', mentions: 167, sentiment: 86, trend: '+4%' },
    { name: 'Parking', mentions: 89, sentiment: 55, trend: '-2%' },
  ],
  finance: [
    { name: 'Returns', mentions: 145, sentiment: 88, trend: '+4%' },
    { name: 'Advisory Quality', mentions: 178, sentiment: 94, trend: '+3%' },
    { name: 'Fee Transparency', mentions: 134, sentiment: 72, trend: '+2%' },
    { name: 'Communication', mentions: 112, sentiment: 82, trend: '-1%' },
    { name: 'Reporting', mentions: 98, sentiment: 76, trend: '+5%' },
  ],
};

// Industry-specific review platforms
const INDUSTRY_PLATFORMS = {
  hospitality: [
    { name: 'Google', icon: 'G', rating: 4.7, reviews: 856, trend: '+0.1', color: '#4285f4' },
    { name: 'TripAdvisor', icon: 'TA', rating: 4.5, reviews: 234, trend: '+0.3', color: '#00aa6c' },
    { name: 'Yelp', icon: 'Y', rating: 4.4, reviews: 89, trend: '-0.1', color: '#c41200' },
    { name: 'Facebook', icon: 'FB', rating: 4.8, reviews: 68, trend: '+0.2', color: '#1877f2' },
  ],
  property: [
    { name: 'Google', icon: 'G', rating: 4.7, reviews: 312, trend: '+0.2', color: '#4285f4' },
    { name: 'RateMyAgent', icon: 'RM', rating: 4.6, reviews: 189, trend: '+0.3', color: '#ff6b00' },
    { name: 'Facebook', icon: 'FB', rating: 4.8, reviews: 98, trend: '+0.1', color: '#1877f2' },
  ],
  healthcare: [
    { name: 'Google', icon: 'G', rating: 4.8, reviews: 456, trend: '+0.1', color: '#4285f4' },
    { name: 'HealthEngine', icon: 'HE', rating: 4.6, reviews: 234, trend: '+0.2', color: '#00bcd4' },
    { name: 'Hotdoc', icon: 'HD', rating: 4.7, reviews: 178, trend: '+0.3', color: '#4caf50' },
  ],
  technology: [
    { name: 'Google', icon: 'G', rating: 4.7, reviews: 89, trend: '+0.2', color: '#4285f4' },
    { name: 'Clutch', icon: 'CL', rating: 4.6, reviews: 45, trend: '+0.1', color: '#17313b' },
    { name: 'GoodFirms', icon: 'GF', rating: 4.5, reviews: 34, trend: '+0.3', color: '#f57c00' },
  ],
};

const buildSentimentData = (industry) => {
  const reviews = getIndustryData(INDUSTRY_REVIEWS, industry) || [];
  const themes = INDUSTRY_THEMES[industry] || INDUSTRY_THEMES.hospitality;
  const platforms = INDUSTRY_PLATFORMS[industry] || INDUSTRY_PLATFORMS.hospitality;
  return {
    overall: {
      score: 4.6,
      totalReviews: platforms.reduce((s, p) => s + p.reviews, 0),
      thisMonth: 48,
      sentiment: { positive: 78, neutral: 15, negative: 7 },
      trend: '+0.2',
      responseRate: 92,
      avgResponseTime: '2.3 hours'
    },
    platforms,
    recentReviews: reviews,
    themes,
    trendData: [
      { month: 'Jul', score: 4.3, reviews: 35 },
      { month: 'Aug', score: 4.4, reviews: 42 },
      { month: 'Sep', score: 4.5, reviews: 38 },
      { month: 'Oct', score: 4.4, reviews: 45 },
      { month: 'Nov', score: 4.6, reviews: 52 },
      { month: 'Dec', score: 4.6, reviews: 48 },
    ]
  };
};

const sentimentStyles = {
  positive: { icon: Smile, gradient: 'from-emerald-400 to-[#0066CC]', shadow: 'shadow-emerald-500/25', bg: 'bg-emerald-100', text: 'text-emerald-600' },
  neutral: { icon: Meh, gradient: 'from-amber-400 to-orange-500', shadow: 'shadow-amber-500/25', bg: 'bg-amber-100', text: 'text-amber-600' },
  negative: { icon: Frown, gradient: 'from-red-400 to-rose-500', shadow: 'shadow-red-500/25', bg: 'bg-red-100', text: 'text-red-600' },
};

const CustomerSentimentTracker = ({
  industry = 'hospitality',
  className = '',
  compact = false,
  onViewDetails,
  onRespondToReview
}) => {
  const [activeTab, setActiveTab] = useState('overview');
  const { overall, platforms, recentReviews, themes, trendData } = buildSentimentData(industry);

  if (compact) {
    return (
      <div 
        className={`relative overflow-hidden rounded-3xl bg-white/70 backdrop-blur-2xl border border-white/30 shadow-[0_8px_32px_rgb(0,0,0,0.06),inset_0_0_0_1px_rgba(255,255,255,0.5)] p-5 transition-all duration-300 hover:shadow-[0_12px_40px_rgb(0,0,0,0.1)] hover:-translate-y-0.5 ${className}`} 
        data-testid="sentiment-tracker-compact"
      >
        <div className="absolute inset-0 bg-gradient-to-br from-white/40 via-transparent to-transparent pointer-events-none" />
        <div className="relative">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2.5">
              <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-blue-400 to-indigo-500 flex items-center justify-center shadow-lg shadow-blue-500/20">
                <MessageSquare className="w-4 h-4 text-white" />
              </div>
              <h4 className="font-semibold text-gray-900 tracking-tight">Customer Sentiment</h4>
            </div>
            <div className="flex items-center gap-1.5">
              <Star className="w-4 h-4 text-amber-400 fill-amber-400" />
              <span className="text-sm font-bold text-gray-900">{overall.score}</span>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <div className="flex-1">
              <Progress value={overall.sentiment.positive} className="h-2" />
              <div className="flex justify-between mt-1.5 text-[10px]">
                <span className="text-emerald-600 font-medium">{overall.sentiment.positive}% positive</span>
                <span className="text-red-600 font-medium">{overall.sentiment.negative}% negative</span>
              </div>
            </div>
            <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-bold bg-emerald-100 text-emerald-700 border border-emerald-200 whitespace-nowrap">{overall.trend}</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div 
      className={`relative overflow-hidden rounded-3xl bg-white/70 backdrop-blur-2xl border border-white/30 shadow-[0_8px_32px_rgb(0,0,0,0.06),inset_0_0_0_1px_rgba(255,255,255,0.5)] p-6 transition-all duration-300 hover:shadow-[0_12px_40px_rgb(0,0,0,0.1)] ${className}`} 
      data-testid="sentiment-tracker-widget"
    >
      {/* Background gradient orb */}
      <div className="absolute -top-20 -right-20 w-56 h-56 bg-gradient-to-br from-blue-100/50 to-indigo-200/30 rounded-full blur-3xl pointer-events-none" />
      <div className="absolute inset-0 bg-gradient-to-br from-white/40 via-transparent to-transparent pointer-events-none" />
      
      <div className="relative">
        {/* Header */}
        <div className="flex items-center justify-between mb-5">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-2xl bg-gradient-to-br from-blue-400 to-indigo-500 flex items-center justify-center shadow-lg shadow-blue-500/25">
              <MessageSquare className="w-5 h-5 text-white" />
            </div>
            <div className="flex items-center gap-2.5">
              <h3 className="font-semibold text-gray-900 tracking-tight">Customer Sentiment</h3>
              <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold bg-blue-50 text-blue-700 border border-blue-100 whitespace-nowrap">{overall.totalReviews} reviews</span>
            </div>
          </div>
          <button 
            onClick={onViewDetails}
            className="flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-gray-500 hover:text-gray-900 hover:bg-gray-100 rounded-full transition-colors whitespace-nowrap"
          >
            All Reviews <ChevronRight className="w-3 h-3 ml-1" />
          </button>
        </div>

        {/* Overall Score */}
 <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-5">
          <div className="col-span-1 p-4 rounded-2xl bg-gradient-to-br from-amber-50/80 to-yellow-50/50 border border-amber-100 text-center">
            <div className="flex items-center justify-center gap-1.5 mb-1">
              <Star className="w-5 h-5 text-amber-400 fill-amber-400" />
              <span className="text-2xl font-bold text-amber-700">{overall.score}</span>
            </div>
            <p className="text-[10px] text-amber-600 font-medium">Overall Rating</p>
            <span className={`text-xs font-semibold ${overall.trend.startsWith('+') ? 'text-emerald-600' : 'text-red-600'}`}>
              {overall.trend} this month
            </span>
          </div>
          <div className="col-span-3 p-4 rounded-2xl bg-gradient-to-r from-gray-50/80 to-white/60 border border-gray-100/50">
            <div className="flex items-center justify-between mb-3">
              <span className="text-xs font-semibold text-gray-600">Sentiment Breakdown</span>
              <span className="text-xs text-gray-500">{overall.thisMonth} reviews this month</span>
            </div>
            <div className="space-y-2">
              <div className="flex items-center gap-3">
                <div className="w-7 h-7 rounded-lg bg-gradient-to-br from-emerald-400 to-[#0066CC] flex items-center justify-center shadow-md shadow-emerald-500/20">
                  <Smile className="w-4 h-4 text-white" />
                </div>
                <div className="flex-1 h-2.5 bg-gray-200 rounded-full overflow-hidden">
                  <div className="h-full bg-gradient-to-r from-emerald-400 to-[#0066CC] rounded-full" style={{ width: `${overall.sentiment.positive}%` }} />
                </div>
                <span className="text-xs font-bold text-emerald-600 w-12 text-right">{overall.sentiment.positive}%</span>
              </div>
              <div className="flex items-center gap-3">
                <div className="w-7 h-7 rounded-lg bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center shadow-md shadow-amber-500/20">
                  <Meh className="w-4 h-4 text-white" />
                </div>
                <div className="flex-1 h-2.5 bg-gray-200 rounded-full overflow-hidden">
                  <div className="h-full bg-gradient-to-r from-amber-400 to-orange-500 rounded-full" style={{ width: `${overall.sentiment.neutral}%` }} />
                </div>
                <span className="text-xs font-bold text-amber-600 w-12 text-right">{overall.sentiment.neutral}%</span>
              </div>
              <div className="flex items-center gap-3">
                <div className="w-7 h-7 rounded-lg bg-gradient-to-br from-red-400 to-rose-500 flex items-center justify-center shadow-md shadow-red-500/20">
                  <Frown className="w-4 h-4 text-white" />
                </div>
                <div className="flex-1 h-2.5 bg-gray-200 rounded-full overflow-hidden">
                  <div className="h-full bg-gradient-to-r from-red-400 to-rose-500 rounded-full" style={{ width: `${overall.sentiment.negative}%` }} />
                </div>
                <span className="text-xs font-bold text-red-600 w-12 text-right">{overall.sentiment.negative}%</span>
              </div>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="flex justify-start overflow-x-auto scrollbar-hide [&>*]:flex-shrink-0 md:grid md:grid-cols-4 h-10 mb-4 p-1 bg-gray-100/50 rounded-2xl">
            <TabsTrigger value="overview" className="text-xs rounded-xl data-[state=active]:bg-white data-[state=active]:shadow-sm">Overview</TabsTrigger>
            <TabsTrigger value="reviews" className="text-xs rounded-xl data-[state=active]:bg-white data-[state=active]:shadow-sm">Recent</TabsTrigger>
            <TabsTrigger value="themes" className="text-xs rounded-xl data-[state=active]:bg-white data-[state=active]:shadow-sm">Themes</TabsTrigger>
            <TabsTrigger value="platforms" className="text-xs rounded-xl data-[state=active]:bg-white data-[state=active]:shadow-sm">Platforms</TabsTrigger>
          </TabsList>

          {/* Overview Tab */}
          <TabsContent value="overview" className="mt-0">
            <div className="h-36 mb-4 p-3 rounded-2xl bg-gradient-to-br from-gray-50/50 to-white/30 border border-gray-100">
              <ResponsiveContainer width="100%" height={120}>
                <AreaChart data={trendData}>
                  <defs>
                    <linearGradient id="sentimentGradient" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.3}/>
                      <stop offset="95%" stopColor="#3b82f6" stopOpacity={0}/>
                    </linearGradient>
                  </defs>
                  <XAxis dataKey="month" axisLine={false} tickLine={false} tick={{ fontSize: 10, fill: '#9ca3af' }} />
                  <YAxis domain={[4, 5]} axisLine={false} tickLine={false} tick={{ fontSize: 10, fill: '#9ca3af' }} width={30} />
                  <Tooltip contentStyle={{ fontSize: '11px', borderRadius: '12px', border: '1px solid rgba(0,0,0,0.05)', boxShadow: '0 4px 20px rgba(0,0,0,0.1)' }} />
                  <Area type="monotone" dataKey="score" stroke="#3b82f6" strokeWidth={2} fill="url(#sentimentGradient)" />
                </AreaChart>
              </ResponsiveContainer>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div className="p-3 rounded-2xl bg-gradient-to-br from-emerald-50/80 to-blue-50/50 border border-emerald-100 text-center">
                <p className="text-xl font-bold text-emerald-700">{overall.responseRate}%</p>
                <p className="text-[10px] text-emerald-600 font-medium">Response Rate</p>
              </div>
              <div className="p-3 rounded-2xl bg-gradient-to-br from-blue-50/80 to-indigo-50/50 border border-blue-100 text-center">
                <p className="text-xl font-bold text-blue-700">{overall.avgResponseTime}</p>
                <p className="text-[10px] text-blue-600 font-medium">Avg Response Time</p>
              </div>
            </div>
          </TabsContent>

          {/* Recent Reviews Tab */}
          <TabsContent value="reviews" className="space-y-2.5 mt-0">
            {recentReviews.map(review => {
              const sentimentStyle = sentimentStyles[review.sentiment];
              const SentimentIcon = sentimentStyle.icon;
              return (
                <div key={review.id} className="p-4 rounded-2xl bg-gradient-to-r from-gray-50/80 to-white/60 border border-gray-100/50 transition-all hover:shadow-md">
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-center gap-2.5">
                      <span className="text-lg">{platforms.find(p => p.name === review.platform)?.icon}</span>
                      <div className="flex items-center gap-1">
                        {[...Array(5)].map((_, i) => (
                          <Star 
                            key={`${_?.id ?? _?.name ?? _?.title ?? '_'}-${i}`} 
                            className={`w-3.5 h-3.5 ${i < review.rating ? 'text-amber-400 fill-amber-400' : 'text-gray-300'}`} 
                          />
                        ))}
                      </div>
                      <span className="text-xs text-gray-500">{review.date}</span>
                    </div>
                    <div className={`w-7 h-7 rounded-lg bg-gradient-to-br ${sentimentStyle.gradient} flex items-center justify-center shadow-md ${sentimentStyle.shadow}`}>
                      <SentimentIcon className="w-4 h-4 text-white" />
                    </div>
                  </div>
                  <p className="text-sm text-gray-700 mb-3 line-clamp-2 leading-relaxed">&ldquo;{review.text}&rdquo;</p>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-1.5">
                      {review.themes.map(theme => (
                        <span key={theme} className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold bg-gray-100 text-gray-600 border border-gray-200 whitespace-nowrap">{theme}</span>
                      ))}
                    </div>
                    {!review.responded ? (
                      <button 
                        onClick={() => onRespondToReview?.(review)}
                        className="px-3 py-1.5 text-xs font-medium text-blue-700 bg-blue-100 hover:bg-blue-200 rounded-full transition-colors whitespace-nowrap"
                      >
                        Respond
                      </button>
                    ) : (
                      <span className="inline-flex items-center px-2.5 py-1 rounded-full text-[10px] font-bold bg-emerald-100 text-emerald-700 border border-emerald-200 whitespace-nowrap">Responded</span>
                    )}
                  </div>
                </div>
              );
            })}
          </TabsContent>

          {/* Themes Tab */}
          <TabsContent value="themes" className="space-y-2 mt-0">
            {themes.map(theme => (
              <div key={theme.name} className="flex items-center gap-3 p-3 rounded-2xl bg-gradient-to-r from-gray-50/80 to-white/60 border border-gray-100/50 transition-all hover:shadow-md hover:-translate-y-0.5">
                <div className="flex-1">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-semibold text-gray-900">{theme.name}</span>
                    <span className={`text-xs font-bold ${theme.sentiment >= 80 ? 'text-emerald-600' : theme.sentiment >= 60 ? 'text-amber-600' : 'text-red-600'}`}>
                      {theme.sentiment}% positive
                    </span>
                  </div>
                  <Progress value={theme.sentiment} className="h-2" />
                </div>
                <div className="text-right">
                  <p className="text-xs text-gray-500 font-medium">{theme.mentions} mentions</p>
                  <span className={`text-[11px] font-semibold ${theme.trend.startsWith('+') ? 'text-emerald-600' : 'text-red-600'}`}>
                    {theme.trend}
                  </span>
                </div>
              </div>
            ))}
          </TabsContent>

          {/* Platforms Tab */}
          <TabsContent value="platforms" className="space-y-2.5 mt-0">
            {platforms.map(platform => (
              <div key={platform.name} className="flex items-center gap-4 p-4 rounded-2xl bg-gradient-to-r from-gray-50/80 to-white/60 border border-gray-100/50 transition-all hover:shadow-md hover:-translate-y-0.5">
                <span className="text-2xl">{platform.icon}</span>
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <span className="font-semibold text-gray-900">{platform.name}</span>
                    <span className={`text-xs font-semibold ${platform.trend.startsWith('+') ? 'text-emerald-600' : 'text-red-600'}`}>
                      {platform.trend}
                    </span>
                  </div>
                  <p className="text-xs text-gray-500">{platform.reviews} reviews</p>
                </div>
                <div className="flex items-center gap-1.5">
                  <Star className="w-5 h-5 text-amber-400 fill-amber-400" />
                  <span className="text-xl font-bold text-gray-900">{platform.rating}</span>
                </div>
              </div>
            ))}
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default CustomerSentimentTracker;
